import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import {
  MEMBER_ELIGIBILITY_UNKNOWN,
  memberEligibilityClientDataSchema,
  memberEligibilityDataSchema,
  tolerantEnum,
} from '../members/eligibility';

// ─── eligibility manual review: staff decisions ─────────────────────────────
//
// A staff member who has verified a member waiting on manual eligibility review
// records the outcome against the check they reviewed. Only the "Confirmed"
// decision exists today; the vocabulary is a list so a later "Not confirmed"
// decision extends it rather than replacing it.

export const ELIGIBILITY_REVIEW_DECISIONS = ['confirmed'] as const;
export const eligibilityReviewDecisionKindSchema = z.enum(ELIGIBILITY_REVIEW_DECISIONS);
export type EligibilityReviewDecisionKind = z.infer<typeof eligibilityReviewDecisionKindSchema>;

/** How the staff member verified the member's coverage. Closed list. */
export const ELIGIBILITY_CONFIRM_REASON_CODES = [
  'plan_portal_verified',
  'sponsor_roster_verified',
  'sponsor_confirmed',
  'member_documentation',
  'other',
] as const;
export const eligibilityConfirmReasonCodeSchema = z.enum(ELIGIBILITY_CONFIRM_REASON_CODES);
export type EligibilityConfirmReasonCode = z.infer<typeof eligibilityConfirmReasonCodeSchema>;

/** Longest note a decision may carry, after trimming. Mirrored by a table CHECK. */
export const ELIGIBILITY_REVIEW_DECISION_NOTE_MAX = 500;

/** Why a decision was refused (each answers 409 with the code as the error). */
export const ELIGIBILITY_REVIEW_DECISION_REFUSALS = [
  'eligibility_not_in_review',
  'check_not_latest',
  'sponsor_missing',
  'no_enrolled_programs',
  'enrollment_in_progress',
] as const;
export const eligibilityReviewDecisionRefusalSchema = z.enum(ELIGIBILITY_REVIEW_DECISION_REFUSALS);
export type EligibilityReviewDecisionRefusal = z.infer<
  typeof eligibilityReviewDecisionRefusalSchema
>;

/**
 * The request body. `.strict()`: a REQUEST schema refuses an unknown key
 * (ADR-135 Rule 1). The note is free text that may carry PHI, so it is trimmed,
 * bounded, and required only when the reason is "Other" (there is nothing else
 * to explain that reason).
 */
export const eligibilityReviewDecisionRequestSchema = z
  .object({
    checkId: z.string().uuid(),
    decision: eligibilityReviewDecisionKindSchema,
    reasonCode: eligibilityConfirmReasonCodeSchema,
    note: z.string().trim().min(1).max(ELIGIBILITY_REVIEW_DECISION_NOTE_MAX).optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.reasonCode === 'other' && body.note === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['note'],
        message: 'A note is required when the reason is other',
      });
    }
  });
export type EligibilityReviewDecisionRequest = z.infer<
  typeof eligibilityReviewDecisionRequestSchema
>;

/**
 * One recorded decision as the API returns it. The note is never echoed: it is
 * stored for the record, not shown back. `decidedBy` is the deciding staff
 * member's internal user id.
 */
export const eligibilityReviewDecisionSchema = z.object({
  id: z.string().uuid(),
  checkId: z.string().uuid(),
  decision: eligibilityReviewDecisionKindSchema,
  reasonCode: eligibilityConfirmReasonCodeSchema,
  decidedAt: z.string().datetime({ offset: true }),
  decidedBy: z.string().uuid(),
});
export type EligibilityReviewDecision = z.infer<typeof eligibilityReviewDecisionSchema>;

/**
 * The decision plus the member's eligibility read after it, so a client can
 * replace its cache in one step. Not `.strict()`: a response schema strips
 * unknown keys instead (ADR-135).
 */
export const eligibilityReviewDecisionDataSchema = z.object({
  decision: eligibilityReviewDecisionSchema,
  eligibility: memberEligibilityDataSchema,
});
export type EligibilityReviewDecisionData = z.infer<typeof eligibilityReviewDecisionDataSchema>;

export const eligibilityReviewDecisionResponseSchema = apiEnvelope(
  eligibilityReviewDecisionDataSchema,
);
export type EligibilityReviewDecisionResponse = z.infer<
  typeof eligibilityReviewDecisionResponseSchema
>;

// ─── client (Atlas) schemas ─────────────────────────────────────────────────

/**
 * What Atlas parses: the wire shape with each enum-valued field resolving an
 * unrecognised string to `unknown`, so a server that learns a new decision or
 * reason does not fail an older bundle.
 */
export const eligibilityReviewDecisionClientSchema = eligibilityReviewDecisionSchema.extend({
  decision: tolerantEnum(ELIGIBILITY_REVIEW_DECISIONS, MEMBER_ELIGIBILITY_UNKNOWN),
  reasonCode: tolerantEnum(ELIGIBILITY_CONFIRM_REASON_CODES, MEMBER_ELIGIBILITY_UNKNOWN),
});

export const eligibilityReviewDecisionClientDataSchema = z.object({
  decision: eligibilityReviewDecisionClientSchema,
  eligibility: memberEligibilityClientDataSchema,
});
export type EligibilityReviewDecisionClientData = z.infer<
  typeof eligibilityReviewDecisionClientDataSchema
>;

export const eligibilityReviewDecisionClientResponseSchema = apiEnvelope(
  eligibilityReviewDecisionClientDataSchema,
);
